import { IIncident, IIncidentType } from '../../pages/Incidents/interfaces';
import { IAnalysisResponse } from '../../utils/apollo/responseTypes';
import { IncidentsState } from '../reducers/incidents';
import { IAnalysisState } from '../slices/analyses';
import { generateDefaultChartSettings } from './chartSettings';
import { parseCurrentState } from './chartPersistAndRestore';
import {
  IAnalysisInfoFieldsFragment,
  Maybe,
} from '../../utils/apollo/graphqlSchema';
import { IReportSettingFieldsFragment } from '../../utils/apollo/graphqlSchema';
import { isTransientAnalysisId } from './transientAnalysis';
import { UnreachableCaseError } from 'ts-essentials';
import { IChartSettings } from '../../components/Chart/interfaces';

const getIncidents = (
  analysis: IAnalysisResponse,
  incidentTypes: IncidentsState['incidentTypes'],
) => {
  const mappedIncidents =
    'incidents' in analysis
      ? analysis?.incidents?.reduce<Record<string, IIncident[]>>(
          (acc, currentIncident) => {
            const incidentWithType = {
              ...currentIncident,
              type: {
                ...incidentTypes[currentIncident.type.id],
              },
            };
            const startStrKey = incidentWithType.start;
            if (acc[startStrKey]) {
              acc[startStrKey] = [
                acc[startStrKey],
                { ...incidentWithType },
              ].flat();
            } else {
              acc[startStrKey] = [{ ...incidentWithType }];
            }
            return acc;
          },
          {},
        )
      : {};
  const activeIncidentTypes = {
    ...Object.fromEntries(
      Object.keys(incidentTypes).map((key: IIncidentType['id']) => [
        key,
        false,
      ]),
    ),
    showAll: false,
  };

  return {
    incidents: mappedIncidents,
    defaultActiveIncidentTypes: activeIncidentTypes,
  };
};

export const getAnalysisSettings = ({
  analysis,
  incidentTypes,
  reportSetting,
}: {
  analysis: IAnalysisResponse;
  incidentTypes: IncidentsState['incidentTypes'];
  reportSetting: Maybe<IReportSettingFieldsFragment>;
}): {
  settings: IAnalysisState['settings'];
  defaultChartSettings: IChartSettings;
} => {
  const chartPref = analysis?.pref?.chartPref;
  // Available Incidents might changed by edit/delete.
  // therefore we need to get the current available incidents,
  // and merge the stored with the current ones.
  const { incidents, defaultActiveIncidentTypes } = getIncidents(
    analysis,
    incidentTypes,
  );

  const defaultChartSettings = generateDefaultChartSettings(
    analysis,
    reportSetting,
    defaultActiveIncidentTypes,
  );

  const currentChartSettings =
    isTransientAnalysisId(analysis.id) || !chartPref
      ? null
      : parseCurrentState({
          analysis,
          chartPref,
          defaultChartSettings,
        });

  return {
    settings: {
      incidents,
      chartWidth: 0,
      inspectSubjectId: null,
      chartSettings: currentChartSettings ?? defaultChartSettings,
      chartSettingsSnapshot: currentChartSettings ?? defaultChartSettings,
    },
    defaultChartSettings,
  };
};

export const toAnalysisInfo = (
  resp: IAnalysisInfoFieldsFragment,
): IAnalysisInfoFieldsFragment => {
  const {
    id,
    display,
    description,
    executedAt,
    queryType,
    start,
    end,
    creator,
    datePicker,
  } = resp;

  switch (resp.__typename) {
    case 'Expression': {
      return {
        __typename: resp.__typename,
        query: resp.query,
        timeUnit: resp.timeUnit,
        comparisonConfig: resp.comparisonConfig,
        id,
        display,
        description,
        executedAt,
        queryType,
        start,
        end,
        creator,
        datePicker,
      };
    }
    case 'Funnel': {
      return {
        __typename: resp.__typename,
        query: resp.query,
        timeUnit: resp.timeUnit,
        id,
        display,
        description,
        executedAt,
        queryType,
        start,
        end,
        creator,
        datePicker,
      };
    }
    case 'Retention': {
      return {
        __typename: resp.__typename,
        query: resp.query,
        timeUnit: resp.timeUnit,
        id,
        display,
        description,
        executedAt,
        queryType,
        start,
        end,
        creator,
        datePicker,
      };
    }
    case 'Path': {
      return {
        __typename: resp.__typename,
        query: resp.query,
        timeUnit: resp.timeUnit,
        id,
        display,
        description,
        executedAt,
        queryType,
        start,
        end,
        creator,
        datePicker,
      };
    }
    case 'Prediction': {
      return {
        __typename: resp.__typename,
        query: resp.query,
        timeUnit: resp.timeUnit,
        id,
        display,
        description,
        executedAt,
        queryType,
        start,
        end,
        creator,
        datePicker,
      };
    }
    case 'CohortSample': {
      return {
        __typename: resp.__typename,
        query: resp.query,
        timeUnit: resp.timeUnit,
        id,
        display,
        description,
        executedAt,
        queryType,
        start,
        end,
        creator,
        datePicker,
      };
    }
    case 'DataTable': {
      return {
        __typename: resp.__typename,
        query: resp.query,
        timeUnit: resp.timeUnit,
        comparisonConfig: resp.comparisonConfig,
        id,
        display,
        description,
        executedAt,
        queryType,
        start,
        end,
        creator,
        datePicker,
      };
    }
    case 'Significance': {
      return {
        __typename: resp.__typename,
        query: resp.query,
        timeUnit: resp.timeUnit,
        id,
        display,
        description,
        executedAt,
        queryType,
        start,
        end,
        creator,
        datePicker,
      };
    }
    default: {
      throw new UnreachableCaseError(resp);
    }
  }
};
